/**
 * PHONE NUMBERS, STRICTLY.
 *
 * One reading of a phone number for everything that matches, stores or texts
 * one: E.164 (`+` country code, then the number, 7 to 15 digits in all), or
 * nothing. A value that is not plainly a phone number is REJECTED, never
 * repaired into one: until Oct 2026 zodTPhone put `+1` in front of whatever
 * digits it found and checked the result without anchors, so `+44 7911
 * 123456` was stored as `+1447911123456` and `1-303-555-0142` as
 * `+113035550142`, both of which "passed".
 *
 * Pure and import-free: it runs in the browser bundle too.
 */

/** The default country's dialling code. Only the US (NANP, `+1`) is supported. */
const COUNTRY_CODES = { US: '1' } as const;

export type PhoneCountry = keyof typeof COUNTRY_CODES;

const E164 = /^\+[1-9]\d{6,14}$/;

/** What may separate the digits of a number someone typed: spaces, dashes, dots, slashes, brackets. */
const FORMATTING = /[\s\u00a0\u2010-\u2015()./-]/g;

/**
 * Invisible format characters a copy-paste brings along (bidi marks and
 * overrides from a phone's contact card, zero-width spaces and joiners, a
 * BOM): Unicode category Cf. Never part of a number, so dropped first.
 */
const INVISIBLE = /\p{Cf}/gu;

/**
 * A trailing label saying which phone it is: `(303) 555-0142 cell`,
 * `303-555-0142 (Mobile)`, `303.555.0142 - work`. Only these words: any
 * other letters, an extension (`x12`, `ext. 4`) included, still make the
 * value not a number.
 */
const LABEL = /[\s,;:\u2010-\u2015-]*\(?\s*(?:cell|mobile|home|work|office)\s*\)?\.?$/i;

/** The digits (and a leading `+`) of a typed or pasted value, before it is judged. */
function phone_compact(raw: string | number): string {
  return String(raw).replace(INVISIBLE, '').trim().replace(LABEL, '').trim().replace(FORMATTING, '');
}

/** The longest typed or pasted value read as a phone number. */
const PHONE_INPUT_MAX = 64;

/** A NANP number: `+1` and exactly ten digits. */
const NANP = /^\+1\d{10}$/;

/**
 * The E.164 form of `raw`, or null when it is not a phone number.
 *
 *   `+13035550142`, `+1 (303) 555-0142`, `+44 7911 123456`  as written, formatting removed
 *   `(303) 555-0142`, `303.555.0142`                          10 digits: the default country (US)
 *   `1-303-555-0142`, `13035550142`                           11 digits after a US trunk `1`
 *   `303/555-0142`, `(303) 555-0142 cell`, pasted bidi marks   slashes, a which-phone label, invisible characters
 *
 * Rejected, rather than guessed at: other letters (`1-800-FLOWERS`), extensions
 * (`x12`, `ext. 4`), `tel:` URIs, `00`/`011` international prefixes, a `+1`
 * number that is not ten digits long, anything shorter than 7 or longer than
 * 15 digits.
 */
export function normalizeE164(raw: unknown, defaultCountry: PhoneCountry = 'US'): string | null {
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    return null;
  }

  // No phone number, label and formatting included, is this long. Checked
  // before LABEL, which backtracks badly on a long run of spaces: public
  // forms (/demo-request) pass unbounded input straight here.
  if (String(raw).trim().length > PHONE_INPUT_MAX) {
    return null;
  }

  const compact = phone_compact(raw);

  if (compact.startsWith('+')) {
    if (!E164.test(compact)) {
      return null;
    }

    // A +1 number is NANP: exactly ten digits after the country code.
    if (compact.startsWith('+1') && !NANP.test(compact)) {
      return null;
    }

    return compact;
  }

  if (!/^\d+$/.test(compact)) {
    return null;
  }

  const code = COUNTRY_CODES[defaultCountry];

  if (code === '1') {
    if (compact.length === 10) {
      return `+1${compact}`;
    }

    if (compact.length === 11 && compact.startsWith('1')) {
      return `+${compact}`;
    }
  }

  return null;
}

/** Whether `value` is already a `+1` number in its stored form (TPhone). */
export function isTPhone(value: unknown): value is TPhone {
  return typeof value === 'string' && NANP.test(value);
}

/** The TPhone (`+1` and ten digits) for `raw`, or null for anything else, a non-US number included. */
export function normalizeTPhone(raw: unknown): TPhone | null {
  const phone = normalizeE164(raw, 'US');

  return isTPhone(phone) ? phone : null;
}

/**
 * The form the old zodTPhone stored a number typed with a leading `1`
 * (`1-303-555-0142` was saved as `+113035550142`). Such a row still holds
 * that value, so a lookup by the correct number also tries this one.
 */
export function legacyTrunkTPhone(phone: TPhone): string {
  return `+11${phone.slice(2)}`;
}

/**
 * A stored value read back: what normalizeTPhone accepts, plus the old
 * parser's `+11XXXXXXXXXX` (a typed leading `1`), which is repaired to
 * `+1XXXXXXXXXX`. Nothing else the old parser let through is guessed at.
 */
export function readStoredTPhone(raw: unknown): TPhone | null {
  if (typeof raw === 'string') {
    const compact = phone_compact(raw);

    if (/^\+11\d{10}$/.test(compact)) {
      return `+1${compact.slice(3)}` as TPhone;
    }
  }

  return normalizeTPhone(raw);
}
